import axios from 'axios';
import various from '../General/various';
import { getBlogSessionToken } from './touzenBlogApi';

const failure = (kind, status = null) => Object.assign(new Error('TOUZEN_BLOG_COMMENT_FAILED'), { kind, status });
const positive = (value) => Number.isSafeInteger(value) && value > 0;
const nullableId = (value) => value === null || positive(value);
const reasons = ['anonymous', 'membership_required', 'inactive_member', 'public_name_required', 'allowed'];
const deletionMessages = ['コメント投稿者本人により削除されました', '記事投稿者により削除されました', '管理者により削除されました'];
const date = (value) => typeof value === 'string' && Number.isFinite(Date.parse(value));
const count = (value) => Number.isSafeInteger(value) && value >= 0;
const operationId = (value) => typeof value === 'string' && value.length === 32 && !/[^a-f0-9]/.test(value);

async function request(slug, suffix, { authenticated = false, expectedToken, signal, method = 'get', data } = {}) {
  if (typeof slug !== 'string' || !slug || slug.length > 255 || /[^-a-zA-Z0-9_]/.test(slug)) throw failure('validation', 400);
  const token = authenticated ? getBlogSessionToken() : '';
  if (authenticated && expectedToken !== undefined && token !== expectedToken) throw failure('session_changed');
  if (authenticated && !token) throw failure('unauthenticated', 401);
  try {
    const response = await axios.create({ headers: { common: { Authorization: null } }, auth: null }).request({
      method, url: `${various.currentDomain.replace(/\/?$/, '/')}blog/api/${encodeURIComponent(slug)}/comments/${suffix}`,
      adapter: 'fetch', withCredentials: false, withXSRFToken: false, auth: null, timeout: 15000, signal,
      headers: { 'Content-Type': 'application/json', Authorization: authenticated ? `Bearer ${token}` : null }, ...(data === undefined ? {} : { data }),
    });
    if (signal?.aborted) throw failure('canceled');
    if (authenticated && getBlogSessionToken() !== token) throw failure('session_changed');
    return response.data;
  } catch (error) {
    if (authenticated && getBlogSessionToken() !== token) throw failure('session_changed');
    if (error?.kind) throw error;
    const status = error?.response?.status || null;
    throw failure(({ 400: 'validation', 401: 'unauthenticated', 403: 'forbidden', 404: 'not_found' })[status]
      || (error?.code === 'ERR_CANCELED' ? 'canceled' : ['ECONNABORTED', 'ETIMEDOUT'].includes(error?.code) ? 'timeout' : status ? 'unknown' : 'network'), status);
  }
}

function comment(data, root = false) {
  if (!data || !positive(data.id) || !positive(data.root_id) || !nullableId(data.reply_to_id)
    || !date(data.created_at) || !date(data.updated_at) || !count(data.like_count)
    || ['deleted', 'is_edited', 'can_edit', 'can_delete', 'can_reply'].some((key) => typeof data[key] !== 'boolean')
    || (data.reply_to_author_display_name !== null && typeof data.reply_to_author_display_name !== 'string')
    || (data.deleted ? data.body !== null || data.author_display_name !== null || !deletionMessages.includes(data.deletion_message)
      : typeof data.body !== 'string' || Array.from(data.body).length > 2000 || typeof data.author_display_name !== 'string' || data.deletion_message !== null)
    || (data.reply_to_id === null ? data.root_id !== data.id : data.root_id === data.id)) throw failure('invalid_response');
  const result = Object.fromEntries(['id', 'root_id', 'reply_to_id', 'body', 'author_display_name', 'created_at', 'updated_at', 'is_edited', 'deleted', 'deletion_message',
    'reply_to_author_display_name', 'can_edit', 'can_delete', 'can_reply', 'like_count'].map((key) => [key, data[key]]));
  if (root) {
    if (data.reply_to_id !== null || !Array.isArray(data.replies) || data.replies.length > 20 || !count(data.reply_count)
      || data.reply_count < data.replies.length || !nullableId(data.reply_next_page)) throw failure('invalid_response');
    result.replies = data.replies.map((reply) => comment(reply));
    if (result.replies.some((reply) => reply.root_id !== data.id || reply.reply_to_id === null)
      || new Set(result.replies.map((reply) => reply.id)).size !== result.replies.length) throw failure('invalid_response');
    result.reply_count = data.reply_count; result.reply_next_page = data.reply_next_page;
  }
  return result;
}

export async function listBlogComments(slug, { page = 1, thread = null, ...options } = {}) {
  if (!positive(page) || !nullableId(thread)) throw failure('validation', 400);
  const query = new URLSearchParams({ page: String(page), ...(thread === null ? {} : { thread: String(thread) }) });
  const data = await request(slug, `?${query}`, options);
  const caps = data?.capabilities;
  if (!count(data?.count) || !nullableId(data.next_page) || !nullableId(data.previous_page) || !Array.isArray(data.results) || data.results.length > 20
    || !caps || ['is_authenticated', 'can_comment', 'public_name_required'].some((key) => typeof caps[key] !== 'boolean') || !reasons.includes(caps.reason)
    || (caps.can_comment && (!caps.is_authenticated || caps.public_name_required || caps.reason !== 'allowed'))) throw failure('invalid_response');
  const results = data.results.map((item) => comment(item, thread === null));
  if (new Set(results.map((item) => item.id)).size !== results.length || (thread !== null && results.some((item) => item.root_id !== thread || item.reply_to_id === null))) throw failure('invalid_response');
  return { count: data.count, next_page: data.next_page, previous_page: data.previous_page, results,
    capabilities: { is_authenticated: caps.is_authenticated, can_comment: caps.can_comment, public_name_required: caps.public_name_required, reason: caps.reason } };
}
export async function saveBlogComment(slug, id, body, replyToId = null, options = {}) {
  if ((id !== null && !positive(id)) || !nullableId(replyToId) || typeof body !== 'string' || !body.trim() || Array.from(body).length > 2000) throw failure('validation', 400);
  const data = await request(slug, id === null ? '' : `${id}/`, { ...options, authenticated: true, method: id === null ? 'post' : 'patch',
    data: { body, ...(id === null ? { reply_to_id: replyToId } : {}) } });
  const result = comment(data, data?.reply_to_id === null);
  if ((id !== null && result.id !== id) || (id === null && result.reply_to_id !== replyToId)) throw failure('invalid_response');
  return result;
}
export async function deleteBlogComment(slug, id, options = {}) {
  if (!positive(id)) throw failure('validation', 400);
  const data = await request(slug, `${id}/`, { ...options, authenticated: true, method: 'delete' });
  const result = comment(data, data?.reply_to_id === null);
  if (!result.deleted || result.id !== id) throw failure('invalid_response');
  return result;
}

function like(data, mode, actor) {
  const keys = mode === 'status' ? 'actor_kind,count,liked,operation_status' : 'actor_kind,count,liked';
  if (!data || Object.keys(data).sort().join(',') !== keys || !count(data.count) || !['member', 'guest'].includes(data.actor_kind)
    || (actor && actor !== data.actor_kind) || (mode === 'get' && data.actor_kind === 'guest' ? data.liked !== null
      : mode === 'status' ? data.liked !== null && typeof data.liked !== 'boolean' : typeof data.liked !== 'boolean')
    || (mode === 'status' && (data.actor_kind !== 'guest' || !['confirmed', 'pending', 'unknown'].includes(data.operation_status)
      || (data.operation_status === 'confirmed' && typeof data.liked !== 'boolean')))) throw failure('invalid_response');
  return data;
}
const likeRequest = (slug, id, options) => {
  if (!positive(id)) throw failure('validation', 400);
  return request(slug, `${id}/like/`, options);
};
export const getBlogCommentLike = async (slug, id, options) => like(await likeRequest(slug, id, options), 'get');
export async function setBlogCommentLike(slug, id, input, options) {
  if (!input || !['member', 'guest'].includes(input.actor_kind) || typeof input.liked !== 'boolean'
    || (input.actor_kind === 'guest' && (!operationId(input.operation_id) || typeof input.previously_liked !== 'boolean'))) throw failure('validation', 400);
  const data = { action: 'set', liked: input.liked, ...(input.actor_kind === 'guest' ? { operation_id: input.operation_id, previously_liked: input.previously_liked } : {}) };
  return like(await likeRequest(slug, id, { ...options, method: 'post', data }), 'set', input.actor_kind);
}
export const getBlogCommentLikeOperation = async (slug, id, operation, options) => {
  if (!operationId(operation)) throw failure('validation', 400);
  return like(await likeRequest(slug, id, { ...options, method: 'post', data: { action: 'status', operation_id: operation } }), 'status', 'guest');
};
